import React, { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { IconGripVertical } from '@tabler/icons-react';
import { FIELD_DND_TYPE } from '../FieldCard';
import { SectionFrame, type SectionFrameProps } from './SectionFrame';

/** Authored group drag type, separate from individual questions. */
export const SECTION_DND_TYPE = 'holito-conversation-section';

/** Shared identity carried by group and existing FieldCard drag sources. */
interface PreviewDragItem { id: string; index: number; type: string }

/** Group operations commit fields and metadata atomically. */
interface DraggableSectionProps extends SectionFrameProps {
  index: number;
  onReorder: (from: number, to: number) => void;
  onMoveQuestion: (fieldId: string, sectionId: string, beforeFieldId?: string) => void;
}

/** Stable targets show the exact boundary before committing a drop. */
export function DraggableSection({ index, onReorder, onMoveQuestion, ...frame }: DraggableSectionProps): React.ReactElement {
  const elementRef = useRef<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<'before' | 'after'>('before');
  const [questionPosition, setQuestionPosition] = useState<{ beforeFieldId?: string; top?: number }>({});

  /** Whitespace between questions belongs to its nearest insertion boundary. */
  const questionDestination = (y: number | undefined): { beforeFieldId?: string; top?: number } => {
    const section = elementRef.current;
    if (!section || !frame.expanded || y === undefined) return {};
    const cards = Array.from(section.querySelectorAll<HTMLElement>('[data-field-id]'));
    const sectionTop = section.getBoundingClientRect().top;
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (y < (rect.top + rect.bottom) / 2) return { beforeFieldId: card.dataset.fieldId, top: rect.top - sectionTop - 12 };
    }
    const last = cards.at(-1)?.getBoundingClientRect();
    return last ? { top: last.bottom - sectionTop + 12 } : {};
  };
  const [{ isDragging, sourceId }, drag, preview] = useDrag(() => ({
    type: SECTION_DND_TYPE,
    item: { id: frame.id, index, type: SECTION_DND_TYPE },
    collect: monitor => ({ isDragging: monitor.isDragging(), sourceId: monitor.getHandlerId()?.toString() }),
  }), [frame.id, index]);
  const [{ targetId, isOver, kind }, drop] = useDrop<PreviewDragItem, void, { targetId?: string; isOver: boolean; kind: string | null }>(() => ({
    accept: [SECTION_DND_TYPE, FIELD_DND_TYPE],
    canDrop: item => item.type !== SECTION_DND_TYPE || item.id !== frame.id,
    hover(item, monitor) {
      if (!monitor.isOver({ shallow: true })) return;
      const element = elementRef.current;
      const offset = monitor.getClientOffset();
      if (!element || !offset) return;
      const rect = element.getBoundingClientRect();
      if (item.type === SECTION_DND_TYPE) setPlacement(offset.y < (rect.top + rect.bottom) / 2 ? 'before' : 'after');
      else setQuestionPosition(questionDestination(offset.y));
    },
    drop(item, monitor) {
      if (monitor.didDrop()) return;
      if (item.type === SECTION_DND_TYPE) {
        const rect = elementRef.current?.getBoundingClientRect();
        const offset = monitor.getClientOffset();
        const after = rect && offset ? offset.y >= (rect.top + rect.bottom) / 2 : placement === 'after';
        const boundary = index + (after ? 1 : 0);
        const destination = boundary - (item.index < boundary ? 1 : 0);
        if (destination !== item.index) onReorder(item.index, destination);
      } else {
        const destination = questionDestination(monitor.getClientOffset()?.y);
        if (destination.beforeFieldId) onMoveQuestion(item.id, frame.id, destination.beforeFieldId);
        else onMoveQuestion(item.id, frame.id);
      }
    },
    collect: monitor => ({ targetId: monitor.getHandlerId()?.toString(), isOver: monitor.isOver({ shallow: true }) && monitor.canDrop(), kind: monitor.getItemType()?.toString() ?? null }),
  }), [frame.id, frame.expanded, index, onReorder, onMoveQuestion, placement]);
  const sectionDrop = isOver && kind === SECTION_DND_TYPE;
  const questionDrop = isOver && kind === FIELD_DND_TYPE;
  return <SectionFrame {...frame} sectionRef={element => { elementRef.current = element; drop(element); preview(element?.querySelector('.section-heading') ?? element); }} dropTargetId={targetId} dragging={isDragging}
    dropIndicator={<>
      <div data-testid={sectionDrop ? 'section-drop-indicator' : undefined} data-placement={placement} data-active={sectionDrop}
        className="section-drop-indicator" role={sectionDrop ? 'status' : undefined} aria-hidden={!sectionDrop}>
        <span className="sr-only">{placement === 'before' ? 'Before' : 'After'} {frame.title}</span>
      </div>
      <div className="section-question-drop" data-active={questionDrop} role={questionDrop ? 'status' : undefined} aria-hidden={!questionDrop}
        style={!frame.expanded ? { bottom: -12 } : questionPosition.top === undefined ? undefined : { top: questionPosition.top, bottom: 'auto' }}>
        <span className="sr-only">In {frame.title}</span>
      </div>
    </>}
    dragHandle={<button ref={element => { drag(element); }} type="button" className="section-drag-handle icon-button" data-drag-source-id={sourceId}
      aria-label={`Drag section ${frame.title}`} title="Drag section"><IconGripVertical size={16} /></button>} />;
}
